import { Stack, Typography } from '@mui/material'
import { C1CircularProgress } from 'c1ui/components/C1CircularProgress/C1CircularProgress'
import { C1DrawerInner } from 'components/core/drawer/C1DrawerInner'
import { APIError } from 'components/core/errors/api'
import { C1Icon } from 'components/core/icons/C1Icon'
import { C1Link } from 'components/core/links/C1Link'
import { TaskViewLinkV2 } from 'components/core/task-list-v2/task-list-v2-components/TaskViewLinkV2'
import { ClosableProps } from 'components/no-ui/context/modal-provider'
import { AppUserDisplayNameWithFetch } from 'components/pages/app/users/app-user-display-name'
import {
    getTicketId,
    GrantOriginSimplifiedType,
    simplifyGrantOrigins,
} from 'components/pages/entitlement/grant-origin/GrantOrigin'
import { C1AppId, ManualCatalogResourceTypeId, ManualGroupResourceTypeId } from 'lib/constants/system'
import { routes } from 'lib/routes'
import { ClipboardList, ContactRound, Users } from 'lucide-react'
import { GrantOriginWithData, SearchGrantReasonsRequest } from 'pbts/c1/api/app/v1/app_entitlement_user_binding'
import { SearchAppResourcesRequest } from 'pbts/c1/api/app/v1/app_resource'
import { TaskSearchRequest } from 'pbts/c1/api/task/v1/search'
import { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { appResourceSearchApi } from 'redux_toolkit/app-resources'
import { searchGrantReasonsApi } from 'redux_toolkit/entitlements'
import { tasksSearchApi } from 'redux_toolkit/tasks'

type GrantOriginDrawerProps = {
    appId: string;
    appEntitlementId: string;
    appUserId: string;
} & ClosableProps

type GrantOriginListProps = {
    type: 'catalog' | 'group';
    ids: string[];
};

const DisplayGrantOriginList = ({ type, ids }: GrantOriginListProps): ReactNode => {
    const resourceTypeId =
        type === 'catalog' ? ManualCatalogResourceTypeId : ManualGroupResourceTypeId

    const {
        data: appResources,
        isLoading: appResourcesLoading,
        isError: appResourcesError,
        error: appResourcesErrorData,
    } = appResourceSearchApi.useSearchAppResources(
        SearchAppResourcesRequest.create({
            refs: ids.map((id) => ({
                appId: C1AppId,
                appResourceTypeId: resourceTypeId,
                id,
            })),
        }),
    )

    if (appResourcesError) {
        return <APIError err={appResourcesErrorData} />
    }

    if (!appResources) {
        return appResourcesLoading ? <C1CircularProgress /> : null
    }

    if (!appResources[0]?.length) {
        return null
    }

    const IconComponent =
        type === 'catalog' ? ContactRound : Users
    const headerText =
        type === 'catalog' ? (
            <FormattedMessage
                id="grant_origins_drawer_catalog_header"
                defaultMessage="Access profile enrollment"
            />
        ) : (
            <FormattedMessage
                id="grant_origins_drawer_group_header"
                defaultMessage="Group membership"
            />
        )

    const getHref = (resourceId: string): ReturnType<typeof routes.accessProfiles.detail> => {
        return type === 'catalog'
            ? routes.accessProfiles.detail(resourceId)
            : routes.directory.groups.detail(C1AppId, resourceId)
    }

    return (
        <Stack spacing={2} direction="column">
            <Stack spacing={1} direction="row">
                <C1Icon icon={IconComponent} />
                <Typography variant="h6">{headerText}</Typography>
            </Stack>
            <Stack spacing={1} direction="column">
                {appResources[0].map((resource, index) => (
                    <Typography key={index}>
                        <C1Link href={getHref(resource.id)}>
                            {resource.displayName}
                        </C1Link>
                    </Typography>
                ))}
            </Stack>
        </Stack>
    )
}

const DisplayTicketGrantOriginList = ({ requestedIds }: { requestedIds: string[] }): ReactNode => {
    const ticketIds = requestedIds.map((requestedId) => getTicketId(requestedId)).filter((ticketId) => !!ticketId)
    if (ticketIds.length < 1) {
        return null
    }

    const {
        data: tasks,
        isLoading: tasksLoading,
        isError: tasksError,
        error: tasksErrorData,
    } = tasksSearchApi.useSearch(TaskSearchRequest.create({
        refs: ticketIds.map((ticketId) => ({
            id: ticketId,
        })),
    }))

    if (tasksError) {
        return <APIError err={tasksErrorData} />
    }

    if (!tasks) {
        return tasksLoading ? <C1CircularProgress /> : null
    }

    if (tasks.length !== 1 || tasks[0].length < 1) {
        return null
    }

    return <Stack spacing={2} direction="column">
        <Stack spacing={1} direction="row">
            <C1Icon icon={ClipboardList} />
            <Typography variant="h6">
                <FormattedMessage id="grant_origins_drawer_tasks_header" defaultMessage="Access request" />
            </Typography>
        </Stack>
        <Stack spacing={1} direction="column">
            {tasks[0].map((task, index) => {
                return <TaskViewLinkV2 task={task} key={index} />
            })}
        </Stack>
    </Stack>
}

const DisplayGrantOriginsList = ({ grantOrigin }: { grantOrigin: GrantOriginWithData }): ReactNode => {
    const dedupedOriginData = Array.from(new Set(grantOrigin.originData))
    switch (grantOrigin.originType) {
        case GrantOriginSimplifiedType.Catalog:
            return <DisplayGrantOriginList type="catalog" ids={dedupedOriginData} />
        case GrantOriginSimplifiedType.Group:
            return <DisplayGrantOriginList type="group" ids={dedupedOriginData} />
        case GrantOriginSimplifiedType.Ticket:
            return <DisplayTicketGrantOriginList requestedIds={dedupedOriginData} />
        default:
            return <></>
    }
}

const DisplayGrantOriginsForDrawer = ({
    appId,
    appEntitlementId,
    appUserId,
}: { appId: string, appEntitlementId: string, appUserId: string }): ReactNode => {
    const {
        data: grantReasons,
        isLoading: grantReasonsLoading,
        isError: grantReasonsError,
        error: grantReasonsErrorData,
    } = searchGrantReasonsApi.useSearchGrantReasons(SearchGrantReasonsRequest.create({
        appId,
        appEntitlementId,
        appUserId,
    }))

    if (grantReasonsError) {
        return <APIError err={grantReasonsErrorData} />
    }

    if (!grantReasons) {
        return grantReasonsLoading ? <C1CircularProgress /> : null
    }

    const simplifiedOrigins = simplifyGrantOrigins(grantReasons)

    if (simplifiedOrigins.length < 1) {
        return null
    }

    return <Stack spacing={4} direction="column">
        <Typography>
            <FormattedMessage id="grant_origins_drawer_header" defaultMessage="Here's how this user was granted access to this entitlement" />
            :
        </Typography>
        {simplifiedOrigins.map((origin, index) => {
            return <DisplayGrantOriginsList key={index} grantOrigin={origin} />
        })}
    </Stack>
}

export const GrantOriginDrawer = ({
    appId,
    appEntitlementId,
    appUserId,
}: GrantOriginDrawerProps): ReactNode => {
    return <C1DrawerInner>
        <C1DrawerInner.Header>
            <Stack spacing={4} my={4}>
                <Typography variant="h2" component="div">
                    <FormattedMessage
                        id="component_grant_origin_title"
                        defaultMessage="Grant origin"
                    />
                </Typography>
                <AppUserDisplayNameWithFetch appId={appId} id={appUserId} />
            </Stack>
        </C1DrawerInner.Header>
        <C1DrawerInner.Content>
            <DisplayGrantOriginsForDrawer
                appId={appId}
                appEntitlementId={appEntitlementId}
                appUserId={appUserId}
            />
        </C1DrawerInner.Content>
    </C1DrawerInner>
}
